import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React from 'react'

import T from 'T'
import { NOTIFICATION_SOURCE, type Notification } from 'types/modules/notification-center/notification'

import { BELL_BADGE_SIZE } from './notificationCenterConstants'
import { bodyFor } from './notificationCenterViews'
import NotificationSeverityBadge from './NotificationSeverityBadge'
import NotificationSummary from './NotificationSummary'
import OptimizationOutcomeChip from './OptimizationOutcomeChip'
import { useNotificationFacts } from './useNotificationFacts'
import { reviewActionVariant, useOptimizationReview } from './useOptimizationReview'

const ACTION_SX = { alignSelf: 'flex-start', mt: 0.5, ml: 4.25 }

// Indented to the badge gutter so it lines up with the title and the action below it.
const OPTIONS_SX = { ml: 4.25, mt: 0.25 }

type CRMNotificationItemProps = {
  notification: Notification
}

const CRMNotificationItem = ({ notification }: CRMNotificationItemProps) => {
  const facts = useNotificationFacts(notification)
  const { reviewUrl, isApplied, isRejected, outcome, optionsUsed } = useOptimizationReview(notification)

  // Elsewhere the title is the event and the entity is only who it happened to, so it cannot lead.
  const routeLabel = notification.source === NOTIFICATION_SOURCE.ROUTE_OPTIMIZATION ? notification.entityLabel : undefined

  return (
    <Stack sx={{ minWidth: 0, flex: 1 }}>
      <NotificationSummary
        badge={<NotificationSeverityBadge notification={notification} size={BELL_BADGE_SIZE} />}
        description={routeLabel ? notification.title : bodyFor(notification)}
        facts={routeLabel ? facts.filter(fact => fact.key !== 'route') : facts}
        occurredAt={notification.occurredAt}
        status={<OptimizationOutcomeChip outcome={outcome} />}
        title={routeLabel ?? notification.title}
      />
      {optionsUsed && (
        <Typography noWrap color="text.disabled" sx={OPTIONS_SX} variant="caption">
          {optionsUsed}
        </Typography>
      )}
      {/* A span, not a button: the row is already the anchor, and nesting one would cost its tab-click. */}
      {reviewUrl && (
        <Button
          component="span"
          size="small"
          startIcon={isApplied ? <CheckCircleOutlineIcon /> : <CompareArrowsIcon />}
          sx={ACTION_SX}
          variant={reviewActionVariant({ isApplied, isRejected })}
        >
          {isApplied ? T.VIEW_APPLIED_OPTIMIZATION : T.REVIEW_OPTIMIZATION}
        </Button>
      )}
    </Stack>
  )
}

export default CRMNotificationItem
